import { useState, useEffect } from 'react';

const MOBILE_MAX = 767;
const PHONE_MAX = 480;
const TABLET_MAX = 1023;

function readViewport() {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const height = typeof window !== 'undefined' ? window.innerHeight : 768;
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  /* innerWidth for breakpoints — stable while scrolling (visualViewport fires scroll on iOS) */
  const layoutWidth = width;
  const layoutHeight = vv?.height ? Math.round(vv.height) : height;

  return {
    width,
    height,
    layoutWidth,
    layoutHeight,
    isPhone: layoutWidth <= PHONE_MAX,
    isMobile: layoutWidth <= MOBILE_MAX,
    isTablet: layoutWidth > MOBILE_MAX && layoutWidth <= TABLET_MAX,
    isDesktop: layoutWidth > TABLET_MAX,
  };
}

function viewportEqual(a, b) {
  return (
    a.width === b.width &&
    a.height === b.height &&
    a.layoutWidth === b.layoutWidth &&
    a.layoutHeight === b.layoutHeight &&
    a.isPhone === b.isPhone &&
    a.isMobile === b.isMobile &&
    a.isTablet === b.isTablet &&
    a.isDesktop === b.isDesktop
  );
}

export function useViewport() {
  const [viewport, setViewport] = useState(() =>
    typeof window !== 'undefined'
      ? readViewport()
      : {
          width: 1024,
          height: 768,
          layoutWidth: 1024,
          layoutHeight: 768,
          isPhone: false,
          isMobile: false,
          isTablet: false,
          isDesktop: true,
        }
  );

  useEffect(() => {
    const handleResize = () => {
      setViewport((prev) => {
        const next = readViewport();
        return viewportEqual(prev, next) ? prev : next;
      });
    };

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);

    const mobileMq = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const phoneMq = window.matchMedia(`(max-width: ${PHONE_MAX}px)`);
    const onMq = () => handleResize();
    mobileMq.addEventListener?.('change', onMq);
    phoneMq.addEventListener?.('change', onMq);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
      mobileMq.removeEventListener?.('change', onMq);
      phoneMq.removeEventListener?.('change', onMq);
    };
  }, []);

  return viewport;
}
